/**
 * FACELIFT 2026: product page behaviour.
 *
 * Loaded by sections/main-product.liquid only when the theme setting "Enable facelift 2026" is on.
 *
 * - <bb-pdp-slider>: horizontal / vertical scroller with prev / next buttons and edge fades (color tiles,
 *   gallery thumbnails, upsell cards). Keeps its scroll position when Impact re-renders it on variant change.
 * - <bb-pdp-siblings>: the "Wähle eine Option" dropdown (overlay on desktop, accordion on mobile).
 * - <bb-pdp-personalize>: looks up the Zakeke ("-SU") product of the selected variant and shows the button.
 * - <bb-pdp-sticky-atc>: sticky add to cart, keeps clear of the Shopify Inbox chat button.
 * - .bb-pdp-quantity: disables the minus button at the minimum quantity.
 * - [data-bb-reviews-link]: clicking the rating scrolls to the reviews section.
 */

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------------------------------------------------------------------------------------------------------------------
   Slider
   --------------------------------------------------------------------------------------------------------------------- */

const sliderPositions = new Map();

class BbPdpSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.prevButton = this.querySelector(':scope > [data-slider-prev]');
    this.nextButton = this.querySelector(':scope > [data-slider-next]');

    if (!this.track) {
      return;
    }

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    const saved = sliderPositions.get(this.dataset.sliderId);
    if (saved) {
      this.track.scrollLeft = saved.left;
      this.track.scrollTop = saved.top;
    }

    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
    this.track.addEventListener('focusin', (event) => this.revealItem(event.target), { signal });
    this.prevButton?.addEventListener('click', () => this.scrollByPage(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.scrollByPage(1), { signal });

    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);

    // Impact hides filtered thumbnails / variants by toggling the hidden attribute
    this.mutationObserver = new MutationObserver(() => this.update());
    this.mutationObserver.observe(this.track, { attributes: true, attributeFilter: ['hidden'], subtree: true, childList: true });

    requestAnimationFrame(() => {
      this.revealItem(this.selectedItem, false);
      this.update();
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  get isVertical() {
    return getComputedStyle(this.track).getPropertyValue('--bb-slider-axis').trim() === 'y';
  }

  get isRtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  get selectedItem() {
    const checked = this.track.querySelector('input:checked');
    if (checked) {
      return checked.nextElementSibling;
    }

    return this.track.querySelector('.is-selected, [aria-current="true"]');
  }

  onScroll() {
    sliderPositions.set(this.dataset.sliderId, { left: this.track.scrollLeft, top: this.track.scrollTop });
    this.update();
  }

  update() {
    if (!this.track) {
      return;
    }

    const vertical = this.isVertical;
    const size = vertical ? this.track.clientHeight : this.track.clientWidth;
    const scrollSize = vertical ? this.track.scrollHeight : this.track.scrollWidth;
    const position = Math.abs(vertical ? this.track.scrollTop : this.track.scrollLeft);
    const overflowing = scrollSize - size > 2;
    const atStart = position <= 2;
    const atEnd = position + size >= scrollSize - 2;

    this.classList.toggle('bb-pdp-slider--vertical', vertical);
    this.classList.toggle('is-overflowing', overflowing);
    this.classList.toggle('is-at-start', atStart);
    this.classList.toggle('is-at-end', atEnd);

    this.setButtonHidden(this.prevButton, !overflowing || atStart, this.nextButton);
    this.setButtonHidden(this.nextButton, !overflowing || atEnd, this.prevButton);
  }

  setButtonHidden(button, hidden, fallback) {
    if (!button || button.hidden === hidden) {
      return;
    }

    // Keep keyboard focus when the focused button disappears at the start or end
    if (hidden && document.activeElement === button) {
      (fallback && !fallback.hidden ? fallback : this.track).focus({ preventScroll: true });
    }

    button.hidden = hidden;
  }

  scrollByPage(direction) {
    const vertical = this.isVertical;
    const distance = (vertical ? this.track.clientHeight : this.track.clientWidth) * 0.8 * direction;
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';

    if (vertical) {
      this.track.scrollBy({ top: distance, behavior });
    } else {
      this.track.scrollBy({ left: this.isRtl ? -distance : distance, behavior });
    }
  }

  revealItem(target, smooth = true) {
    if (!target || !this.track.contains(target)) {
      return;
    }

    // Option values are visually hidden radios followed by their label
    let item = target.matches('input') ? target.nextElementSibling : target;
    while (item && item.parentElement !== this.track) {
      item = item.parentElement;
    }

    if (!item) {
      return;
    }

    const trackRect = this.track.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const padding = 8;
    const behavior = smooth && !prefersReducedMotion() ? 'smooth' : 'auto';

    if (this.isVertical) {
      if (itemRect.top < trackRect.top) {
        this.track.scrollBy({ top: itemRect.top - trackRect.top - padding, behavior });
      } else if (itemRect.bottom > trackRect.bottom) {
        this.track.scrollBy({ top: itemRect.bottom - trackRect.bottom + padding, behavior });
      }
    } else if (itemRect.left < trackRect.left) {
      this.track.scrollBy({ left: itemRect.left - trackRect.left - padding, behavior });
    } else if (itemRect.right > trackRect.right) {
      this.track.scrollBy({ left: itemRect.right - trackRect.right + padding, behavior });
    }
  }
}

if (!customElements.get('bb-pdp-slider')) {
  customElements.define('bb-pdp-slider', BbPdpSlider);
}

/* ---------------------------------------------------------------------------------------------------------------------
   Sibling dropdown
   --------------------------------------------------------------------------------------------------------------------- */

class BbPdpSiblings extends HTMLElement {
  connectedCallback() {
    this.toggleButton = this.querySelector('.bb-pdp-siblings__toggle');
    this.panel = this.querySelector('.bb-pdp-siblings__panel');

    if (!this.toggleButton || !this.panel) {
      return;
    }

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.toggleButton.addEventListener('click', () => this.setOpen(!this.isOpen), { signal });

    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          event.stopPropagation();
          this.setOpen(false);
          this.toggleButton.focus();
        }
      },
      { signal },
    );

    this.addEventListener(
      'focusout',
      (event) => {
        if (this.isOpen && event.relatedTarget && !this.contains(event.relatedTarget)) {
          this.setOpen(false);
        }
      },
      { signal },
    );

    document.addEventListener(
      'click',
      (event) => {
        if (this.isOpen && !this.contains(event.target)) {
          this.setOpen(false);
        }
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  get isOpen() {
    return this.toggleButton.getAttribute('aria-expanded') === 'true';
  }

  setOpen(open) {
    this.toggleButton.setAttribute('aria-expanded', String(open));
    this.panel.inert = !open;
    this.classList.toggle('is-open', open);
  }
}

if (!customElements.get('bb-pdp-siblings')) {
  customElements.define('bb-pdp-siblings', BbPdpSiblings);
}

/* ---------------------------------------------------------------------------------------------------------------------
   Personalisation (Zakeke)
   The customizable twin of a variant is a separate product whose variant SKU is "<sku>-SU". The lookup result is
   cached per session, so the button appears instantly on the next visits and variant changes.
   --------------------------------------------------------------------------------------------------------------------- */

class BbPdpPersonalize extends HTMLElement {
  async connectedCallback() {
    const sku = this.dataset.sku;

    if (!sku) {
      return;
    }

    const result = await this.lookup(sku);

    if (!this.isConnected || !result?.productId) {
      return;
    }

    const params = new URLSearchParams({ pid: result.productId });
    if (result.variantId) {
      params.set('id', result.variantId);
    }
    params.set('locale', window.Shopify?.locale || '');

    const wrapper = document.createElement('div');
    wrapper.className = 'zakake-button-wrapper';
    wrapper.setAttribute('data-zakake-custom', '');

    const inner = document.createElement('div');
    inner.className = 'zakake-button-wrapper-inner';

    const link = document.createElement('a');
    link.className = 'button button--xl bb-pdp-personalize__button bb-rainbow-border';
    link.href = `/apps/zakeke?${params.toString()}`;
    link.textContent = this.dataset.label || '';

    inner.append(link);
    wrapper.append(inner);
    this.replaceChildren(wrapper);
    this.hidden = false;
  }

  async lookup(sku) {
    const cacheKey = `bb-pdp-personalize:${sku}`;

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      // Storage can be unavailable (private mode); the lookup still works
    }

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}search/suggest.json?resources[options][fields]=variants.sku&q=${encodeURIComponent(`${sku}-SU`)}`);
      const data = await response.json();
      const product = data.resources?.results?.products?.[0];
      const result = product ? { productId: product.id, variantId: product.variants?.[0]?.id || null } : { productId: null };

      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(result));
      } catch (error) {
        // Ignore storage errors
      }

      return result;
    } catch (error) {
      return null;
    }
  }
}

if (!customElements.get('bb-pdp-personalize')) {
  customElements.define('bb-pdp-personalize', BbPdpPersonalize);
}

/* ---------------------------------------------------------------------------------------------------------------------
   Sticky add to cart
   Visible once the main product form has scrolled out of view, hidden again when the footer appears (same rule as
   Impact's <product-quick-add>). The position is checked on scroll (throttled to one check per frame) instead of an
   IntersectionObserver, which misses jumps that skip over the form (anchor links, fast flicks).
   The size and position of the Shopify Inbox button (open shadow root) are measured so the sticky elements never
   cover it. The button grows in with an animation (from about 60% size) and bounces now and then, so a box only counts
   once it has stayed the same for a few checks. The sticky elements stay hidden until then, so they never appear in a
   wrong size and snap. Without a chat after CHAT_TIMEOUT, the pill takes the full width (no <shopify-chat>) or keeps the
   theme setting "Chat button width".
   --------------------------------------------------------------------------------------------------------------------- */

const CHAT_TIMEOUT = 6000;
const CHAT_CHECK_INTERVAL = 100;
const CHAT_STABLE_CHECKS = 3;

class BbPdpStickyAtc extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.getAttribute('form'));
    this.footer = document.querySelector('.footer');

    if (!this.form) {
      return;
    }

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    window.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true, signal });
    window.addEventListener(
      'resize',
      () => {
        this.scheduleUpdate();
        this.settleChat();
      },
      { signal },
    );

    this.chatReady = false;
    this.settleChat();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    clearInterval(this.chatCheck);
    cancelAnimationFrame(this.frame);
  }

  scheduleUpdate() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.updateVisibility());
  }

  updateVisibility() {
    const formPassed = this.form.getBoundingClientRect().bottom < 0;
    const footerReached = this.footer ? this.footer.getBoundingClientRect().top < window.innerHeight : false;
    const visible = this.chatReady && formPassed && !footerReached;

    // The chat button can move (e.g. Shopify's preview bar), so check it again whenever the bar appears
    if (visible && !this.classList.contains('is-visible')) {
      this.settleChat();
    }

    this.classList.toggle('is-visible', visible);
  }

  getChatRect() {
    const chat = document.querySelector('shopify-chat');
    const button = chat?.shadowRoot?.querySelector('.activator') || chat?.shadowRoot?.querySelector('button');
    const rect = button?.getBoundingClientRect();

    return rect && rect.width > 0 && rect.height > 0 ? rect : null;
  }

  /* Checks the chat button every CHAT_CHECK_INTERVAL ms and applies its box once it has stopped changing */
  settleChat() {
    clearInterval(this.chatCheck);

    const startedAt = performance.now();
    let lastKey = '';
    let stableChecks = 0;

    const check = () => {
      const rect = this.getChatRect();
      const key = rect ? [rect.width, rect.height, rect.right, rect.bottom].map(Math.round).join() : '';

      stableChecks = key && key === lastKey ? stableChecks + 1 : 0;
      lastKey = key;

      if (stableChecks >= CHAT_STABLE_CHECKS - 1) {
        this.applyChat(rect);
        this.finishChat();
      } else if (performance.now() - startedAt > CHAT_TIMEOUT) {
        if (!document.querySelector('shopify-chat')) {
          this.classList.add('bb-pdp-sticky--no-chat');
        }

        this.finishChat();
      }
    };

    check();
    this.chatCheck = setInterval(check, CHAT_CHECK_INTERVAL);
  }

  applyChat(rect) {
    this.style.setProperty('--bb-chat-width', `${Math.round(rect.width)}px`);
    this.style.setProperty('--bb-chat-height', `${Math.round(rect.height)}px`);
    this.style.setProperty('--bb-chat-right', `${Math.max(0, Math.round(window.innerWidth - rect.right))}px`);
    this.style.setProperty('--bb-chat-bottom', `${Math.max(0, Math.round(window.innerHeight - rect.bottom))}px`);
    this.classList.remove('bb-pdp-sticky--no-chat');
  }

  finishChat() {
    clearInterval(this.chatCheck);

    if (!this.chatReady) {
      this.chatReady = true;
      this.updateVisibility();
    }
  }
}

if (!customElements.get('bb-pdp-sticky-atc')) {
  customElements.define('bb-pdp-sticky-atc', BbPdpStickyAtc);
}

/* ---------------------------------------------------------------------------------------------------------------------
   Quantity: Impact's <quantity-selector> never disables its buttons. The minus button is disabled at the minimum (1),
   the plus button at a maximum if the input has one. The document listeners run after the buttons' own click handlers.
   --------------------------------------------------------------------------------------------------------------------- */

const syncQuantityButtons = (selector) => {
  const input = selector.querySelector('input');
  const [decreaseButton, increaseButton] = selector.querySelectorAll('button');

  if (!input || !decreaseButton) {
    return;
  }

  const quantity = parseInt(input.value, 10) || 1;
  decreaseButton.disabled = quantity <= (parseInt(input.min, 10) || 1);

  if (increaseButton && input.max) {
    increaseButton.disabled = quantity >= parseInt(input.max, 10);
  }
};

document.querySelectorAll('.bb-pdp-quantity').forEach(syncQuantityButtons);

['click', 'input', 'change'].forEach((type) => {
  document.addEventListener(type, (event) => {
    const selector = event.target.closest?.('.bb-pdp-quantity');
    if (selector) {
      syncQuantityButtons(selector);
    }
  });
});

/* ---------------------------------------------------------------------------------------------------------------------
   Rating: smooth scroll to the reviews section
   The first match below the rating wins. "#bb-reviews" / [data-bb-reviews] is meant for the new reviews section
   (phase 2); the other selectors cover the reviews.io widgets and Shopify's default anchor.
   --------------------------------------------------------------------------------------------------------------------- */

const REVIEW_TARGETS = ['#bb-reviews', '[data-bb-reviews]', '#ReviewsWidget', '.ReviewsWidget', '[id*="reviewsio" i]:not(.shopify-app-block)', '#shopify-product-reviews'];

const findReviewsTarget = (trigger) => {
  for (const selector of REVIEW_TARGETS) {
    const match = Array.from(document.querySelectorAll(selector)).find(
      (element) => !trigger.contains(element) && !element.contains(trigger) && element.getBoundingClientRect().top > trigger.getBoundingClientRect().bottom,
    );

    if (match) {
      return match;
    }
  }

  return null;
};

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-bb-reviews-link]');
  if (!trigger) {
    return;
  }

  const target = findReviewsTarget(trigger);
  if (!target) {
    return;
  }

  event.preventDefault();

  const stickyOffset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-area-height')) || 0;
  const top = target.getBoundingClientRect().top + window.scrollY - stickyOffset - 16;

  window.scrollTo({ top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
});
